import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { formatPrimaryName } from 'utils/names'
import { BaseSubsectionAdapter } from 'components/Assessments/adapters/BaseSubsectionAdapter'
import { AdapterOptions } from 'components/Assessments/adapters/types'
import { SocFormData } from 'components/Assessments/SocAssessment/types/SocFormData'
import { SocFormField } from 'components/Assessments/SocAssessment/types/SocFormField'
import { PatientOverviewFormField } from 'components/Assessments/SocAssessment/sections/PatientOverview/formField'
import { PatientOverviewQuestionnaireItem } from './questionnaire'
import { getAnswerValuesFromItems } from '../../../helpers'

/**
 * Patient Overview subsection adapter for SOC Assessment
 * Handles demographics and care team fields extraction and transformation
 */
export class PatientOverviewAdapter extends BaseSubsectionAdapter<SocFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    options?: AdapterOptions
  ): Partial<SocFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Demographics fields
    const legalName =
      getAnswerValuesFromItems(
        flatAnswersArray,
        PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_DEMOGRAPHICS_LEGAL_NAME
      )?.at(0) || undefined

    const preferredName =
      getAnswerValuesFromItems(
        flatAnswersArray,
        PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_DEMOGRAPHICS_PREFERRED_NAME
      )?.at(0) || undefined

    const dateOfBirth =
      getAnswerValuesFromItems(
        flatAnswersArray,
        PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_DEMOGRAPHICS_DATE_OF_BIRTH
      )?.at(0) || undefined

    const address =
      getAnswerValuesFromItems(
        flatAnswersArray,
        PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_DEMOGRAPHICS_ADDRESS
      )?.at(0) || undefined

    const gender =
      getAnswerValuesFromItems(
        flatAnswersArray,
        PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_DEMOGRAPHICS_GENDER
      )?.at(0) || undefined

    const sex =
      getAnswerValuesFromItems(
        flatAnswersArray,
        PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_DEMOGRAPHICS_SEX
      )?.at(0) || undefined

    const medicaidId =
      getAnswerValuesFromItems(
        flatAnswersArray,
        PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_DEMOGRAPHICS_MEDICAID_ID
      )?.at(0) || undefined

    const ssn =
      getAnswerValuesFromItems(
        flatAnswersArray,
        PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_DEMOGRAPHICS_SSN
      )?.at(0) || undefined

    const primaryLanguage =
      getAnswerValuesFromItems(
        flatAnswersArray,
        PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_DEMOGRAPHICS_PRIMARY_LANGUAGE
      )?.at(0) || undefined

    const religion =
      getAnswerValuesFromItems(
        flatAnswersArray,
        PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_DEMOGRAPHICS_RELIGION
      )?.at(0) || undefined

    const race =
      getAnswerValuesFromItems(
        flatAnswersArray,
        PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_DEMOGRAPHICS_RACE
      )?.at(0) || undefined

    // Care team fields
    const legalGuardianName =
      getAnswerValuesFromItems(
        flatAnswersArray,
        PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_LEGAL_GUARDIAN_NAME
      )?.at(0) || undefined

    const legalGuardianRelation =
      getAnswerValuesFromItems(
        flatAnswersArray,
        PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_LEGAL_GUARDIAN_RELATION_TO_PATIENT
      )?.at(0) || undefined

    const orderingPractitioner =
      getAnswerValuesFromItems(
        flatAnswersArray,
        PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_ORDERING_PRACTITIONER
      )?.at(0) || undefined

    const primaryCareProvider =
      getAnswerValuesFromItems(
        flatAnswersArray,
        PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_PRIMARY_CARE_PROVIDER
      )?.at(0) || undefined

    const orderPreference =
      getAnswerValuesFromItems(
        flatAnswersArray,
        PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_ORDER_PREFERENCE
      ) || undefined

    const socDate =
      getAnswerValuesFromItems(
        flatAnswersArray,
        PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_SOC_DATE
      )?.at(0) || undefined

    const f2fDate =
      getAnswerValuesFromItems(
        flatAnswersArray,
        PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_F2F_DATE
      )?.at(0) || undefined

    const emergencyContactName =
      getAnswerValuesFromItems(
        flatAnswersArray,
        PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_EMERGENCY_CONTACT_NAME
      )?.at(0) || undefined

    const emergencyContactPhone =
      getAnswerValuesFromItems(
        flatAnswersArray,
        PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_EMERGENCY_CONTACT_PHONE
      )?.at(0) || undefined

    const emergencyContactAddress =
      getAnswerValuesFromItems(
        flatAnswersArray,
        PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_EMERGENCY_CONTACT_ADDRESS
      )?.at(0) || undefined

    const alternateCaregiverName =
      getAnswerValuesFromItems(
        flatAnswersArray,
        PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_ALTERNATE_CAREGIVER_NAME
      )?.at(0) || undefined

    const alternateCaregiverPhone =
      getAnswerValuesFromItems(
        flatAnswersArray,
        PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_ALTERNATE_CAREGIVER_PHONE
      )?.at(0) || undefined

    const alternateCaregiverAddress =
      getAnswerValuesFromItems(
        flatAnswersArray,
        PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_ALTERNATE_CAREGIVER_ADDRESS
      )?.at(0) || undefined

    // Handle caregivers array - complex mapping from care team options
    const names =
      getAnswerValuesFromItems(
        flatAnswersArray,
        PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_CAREGIVER_NAME
      ) || []

    const relations =
      getAnswerValuesFromItems(
        flatAnswersArray,
        PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_CAREGIVER_RELATION_TO_PATIENT
      ) || []

    let caregivers: Array<{ name: string; relation: string }> | undefined

    if (options?.careTeam?.caregivers?.length) {
      caregivers = options.careTeam.caregivers
        .map((caregiver, index) => ({
          name: formatPrimaryName(caregiver.name) || names[index] || undefined,
          relation: relations[index] || undefined
        }))
        .filter((caregiver) => caregiver.name || caregiver.relation)
    } else if (names.length > 0 || relations.length > 0) {
      // Fallback: use indexed fields if careTeam not available
      const maxLength = Math.max(names.length, relations.length)
      caregivers = Array.from({ length: maxLength }, (_, index) => ({
        name: names[index] || undefined,
        relation: relations[index] || undefined
      })).filter((caregiver) => caregiver.name || caregiver.relation)
    }

    return {
      [PatientOverviewFormField.PATIENT_OVERVIEW_DEMOGRAPHICS_LEGAL_NAME]: legalName,
      [PatientOverviewFormField.PATIENT_OVERVIEW_DEMOGRAPHICS_PREFERRED_NAME]: preferredName,
      [PatientOverviewFormField.PATIENT_OVERVIEW_DEMOGRAPHICS_DATE_OF_BIRTH]: dateOfBirth,
      [PatientOverviewFormField.PATIENT_OVERVIEW_DEMOGRAPHICS_ADDRESS]: address,
      [PatientOverviewFormField.PATIENT_OVERVIEW_DEMOGRAPHICS_GENDER]: gender,
      [PatientOverviewFormField.PATIENT_OVERVIEW_DEMOGRAPHICS_SEX]: sex,
      [PatientOverviewFormField.PATIENT_OVERVIEW_DEMOGRAPHICS_MEDICAID_ID]: medicaidId,
      [PatientOverviewFormField.PATIENT_OVERVIEW_DEMOGRAPHICS_SSN]: ssn,
      [PatientOverviewFormField.PATIENT_OVERVIEW_DEMOGRAPHICS_PRIMARY_LANGUAGE]: primaryLanguage,
      [PatientOverviewFormField.PATIENT_OVERVIEW_DEMOGRAPHICS_RELIGION]: religion,
      [PatientOverviewFormField.PATIENT_OVERVIEW_DEMOGRAPHICS_RACE]: race,
      [PatientOverviewFormField.PATIENT_OVERVIEW_CARE_TEAM_LEGAL_GUARDIAN_NAME]: legalGuardianName,
      [PatientOverviewFormField.PATIENT_OVERVIEW_CARE_TEAM_LEGAL_GUARDIAN_RELATION_TO_PATIENT]:
        legalGuardianRelation,
      [PatientOverviewFormField.PATIENT_OVERVIEW_CARE_TEAM_ORDERING_PRACTITIONER]:
        orderingPractitioner,
      [PatientOverviewFormField.PATIENT_OVERVIEW_CARE_TEAM_PRIMARY_CARE_PROVIDER]:
        primaryCareProvider,
      [PatientOverviewFormField.PATIENT_OVERVIEW_CARE_TEAM_ORDER_PREFERENCE]: orderPreference,
      [PatientOverviewFormField.PATIENT_OVERVIEW_CARE_TEAM_SOC_DATE]: socDate,
      [PatientOverviewFormField.PATIENT_OVERVIEW_CARE_TEAM_F2F_DATE]: f2fDate,
      [PatientOverviewFormField.PATIENT_OVERVIEW_CARE_TEAM_EMERGENCY_CONTACT_NAME]:
        emergencyContactName,
      [PatientOverviewFormField.PATIENT_OVERVIEW_CARE_TEAM_EMERGENCY_CONTACT_PHONE]:
        emergencyContactPhone,
      [PatientOverviewFormField.PATIENT_OVERVIEW_CARE_TEAM_EMERGENCY_CONTACT_ADDRESS]:
        emergencyContactAddress,
      [PatientOverviewFormField.PATIENT_OVERVIEW_CARE_TEAM_ALTERNATE_CAREGIVER_NAME]:
        alternateCaregiverName,
      [PatientOverviewFormField.PATIENT_OVERVIEW_CARE_TEAM_ALTERNATE_CAREGIVER_PHONE]:
        alternateCaregiverPhone,
      [PatientOverviewFormField.PATIENT_OVERVIEW_CARE_TEAM_ALTERNATE_CAREGIVER_ADDRESS]:
        alternateCaregiverAddress,
      [PatientOverviewFormField.PATIENT_OVERVIEW_CARE_TEAM_CAREGIVER]: caregivers
    }
  }

  buildFlatAnswers(): Array<{ linkId: string; value: unknown }> {
    const formData = this.formData
    if (!formData) {
      return []
    }

    const mutations: Array<{ linkId: string; value: unknown }> = [
      {
        linkId: PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_DEMOGRAPHICS_LEGAL_NAME,
        value: this.getFormDataField(SocFormField.PATIENT_OVERVIEW_DEMOGRAPHICS_LEGAL_NAME)
      },
      {
        linkId: PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_DEMOGRAPHICS_PREFERRED_NAME,
        value: this.getFormDataField(SocFormField.PATIENT_OVERVIEW_DEMOGRAPHICS_PREFERRED_NAME)
      },
      {
        linkId: PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_DEMOGRAPHICS_DATE_OF_BIRTH,
        value: this.getFormDataField(SocFormField.PATIENT_OVERVIEW_DEMOGRAPHICS_DATE_OF_BIRTH)
      },
      {
        linkId: PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_DEMOGRAPHICS_ADDRESS,
        value: this.getFormDataField(SocFormField.PATIENT_OVERVIEW_DEMOGRAPHICS_ADDRESS)
      },
      {
        linkId: PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_DEMOGRAPHICS_GENDER,
        value: this.getFormDataField(SocFormField.PATIENT_OVERVIEW_DEMOGRAPHICS_GENDER)
      },
      {
        linkId: PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_DEMOGRAPHICS_SEX,
        value: this.getFormDataField(SocFormField.PATIENT_OVERVIEW_DEMOGRAPHICS_SEX)
      },
      {
        linkId: PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_DEMOGRAPHICS_MEDICAID_ID,
        value: this.getFormDataField(SocFormField.PATIENT_OVERVIEW_DEMOGRAPHICS_MEDICAID_ID)
      },
      {
        linkId: PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_DEMOGRAPHICS_SSN,
        value: this.getFormDataField(SocFormField.PATIENT_OVERVIEW_DEMOGRAPHICS_SSN)
      },
      {
        linkId: PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_DEMOGRAPHICS_PRIMARY_LANGUAGE,
        value: this.getFormDataField(SocFormField.PATIENT_OVERVIEW_DEMOGRAPHICS_PRIMARY_LANGUAGE)
      },
      {
        linkId: PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_DEMOGRAPHICS_RELIGION,
        value: this.getFormDataField(SocFormField.PATIENT_OVERVIEW_DEMOGRAPHICS_RELIGION)
      },
      {
        linkId: PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_DEMOGRAPHICS_RACE,
        value: this.getFormDataField(SocFormField.PATIENT_OVERVIEW_DEMOGRAPHICS_RACE)
      },
      {
        linkId: PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_LEGAL_GUARDIAN_NAME,
        value: this.getFormDataField(SocFormField.PATIENT_OVERVIEW_CARE_TEAM_LEGAL_GUARDIAN_NAME)
      },
      {
        linkId:
          PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_LEGAL_GUARDIAN_RELATION_TO_PATIENT,
        value: this.getFormDataField(
          SocFormField.PATIENT_OVERVIEW_CARE_TEAM_LEGAL_GUARDIAN_RELATION_TO_PATIENT
        )
      },
      {
        linkId: PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_ORDERING_PRACTITIONER,
        value: this.getFormDataField(SocFormField.PATIENT_OVERVIEW_CARE_TEAM_ORDERING_PRACTITIONER)
      },
      {
        linkId: PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_PRIMARY_CARE_PROVIDER,
        value: this.getFormDataField(SocFormField.PATIENT_OVERVIEW_CARE_TEAM_PRIMARY_CARE_PROVIDER)
      },
      {
        linkId: PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_ORDER_PREFERENCE,
        value: this.getFormDataField(SocFormField.PATIENT_OVERVIEW_CARE_TEAM_ORDER_PREFERENCE)
      },
      {
        linkId: PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_SOC_DATE,
        value: this.getFormDataField(SocFormField.PATIENT_OVERVIEW_CARE_TEAM_SOC_DATE)
      },
      {
        linkId: PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_F2F_DATE,
        value: this.getFormDataField(SocFormField.PATIENT_OVERVIEW_CARE_TEAM_F2F_DATE)
      },
      {
        linkId: PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_EMERGENCY_CONTACT_NAME,
        value: this.getFormDataField(SocFormField.PATIENT_OVERVIEW_CARE_TEAM_EMERGENCY_CONTACT_NAME)
      },
      {
        linkId: PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_EMERGENCY_CONTACT_PHONE,
        value: this.getFormDataField(
          SocFormField.PATIENT_OVERVIEW_CARE_TEAM_EMERGENCY_CONTACT_PHONE
        )
      },
      {
        linkId:
          PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_EMERGENCY_CONTACT_ADDRESS,
        value: this.getFormDataField(
          SocFormField.PATIENT_OVERVIEW_CARE_TEAM_EMERGENCY_CONTACT_ADDRESS
        )
      },
      {
        linkId:
          PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_ALTERNATE_CAREGIVER_NAME,
        value: this.getFormDataField(
          SocFormField.PATIENT_OVERVIEW_CARE_TEAM_ALTERNATE_CAREGIVER_NAME
        )
      },
      {
        linkId:
          PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_ALTERNATE_CAREGIVER_PHONE,
        value: this.getFormDataField(
          SocFormField.PATIENT_OVERVIEW_CARE_TEAM_ALTERNATE_CAREGIVER_PHONE
        )
      },
      {
        linkId:
          PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_ALTERNATE_CAREGIVER_ADDRESS,
        value: this.getFormDataField(
          SocFormField.PATIENT_OVERVIEW_CARE_TEAM_ALTERNATE_CAREGIVER_ADDRESS
        )
      }
    ]

    // Handle caregivers array - ensure names and relations stay aligned by index
    // Caregivers is optional, so check if it exists first
    if (this.formDataFieldExists(SocFormField.PATIENT_OVERVIEW_CARE_TEAM_CAREGIVER)) {
      const caregivers = this.getFormDataField(
        SocFormField.PATIENT_OVERVIEW_CARE_TEAM_CAREGIVER
      ) as Array<{ name: string; relation: string }> | undefined

      const validCaregivers = caregivers?.filter(
        (caregiver) => caregiver.name || caregiver.relation
      )

      if (validCaregivers && validCaregivers.length > 0) {
        mutations.push(
          {
            linkId: PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_CAREGIVER_NAME,
            value: validCaregivers.map((caregiver) => caregiver.name || '')
          },
          {
            linkId:
              PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_CAREGIVER_RELATION_TO_PATIENT,
            value: validCaregivers.map((caregiver) => caregiver.relation || '')
          }
        )
      } else {
        mutations.push(
          {
            linkId: PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_CAREGIVER_NAME,
            value: undefined
          },
          {
            linkId:
              PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_CAREGIVER_RELATION_TO_PATIENT,
            value: undefined
          }
        )
      }
    } else {
      // Caregivers field doesn't exist, set to undefined
      mutations.push(
        {
          linkId: PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_CAREGIVER_NAME,
          value: undefined
        },
        {
          linkId:
            PatientOverviewQuestionnaireItem.PATIENT_OVERVIEW_CARE_TEAM_CAREGIVER_RELATION_TO_PATIENT,
          value: undefined
        }
      )
    }

    return mutations
  }
}
